import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import {
  Button,
  SmallCancelButton,
  SmallButton,
} from '../../../constants/base';
import { useStyles, ModalWrapper, Title, Form } from './ShoppersModal.styled';
import { Divider, IconButton, OutlinedInput } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import {
  searchShoppers,
  createShopperRequest,
  createShoppers,
} from '../../../api/exclusiveShoppers';
import { getToken, deleteToken } from '../../../services/auth';
import { createShopper } from '../../../features/shoppers/shoppersSlice';

export const ShoppersModal = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const storeId = useSelector((state) => state.store.id);
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState('');
  const [userId, setUserId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [jobAssignment, setJobAssignment] = useState('delivery');
  const [verify, setVerify] = useState('');
  const [shapyerRequestEmailSent, setShapyerRequestEmailSent] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSearchExclusiveShopper = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    
    if (searchValue === '') {
      return;
    }
    const res = await searchShoppers({
      id: storeId,
      token: getToken(),
      email: searchValue,
    });

    try {
      if (res.count === 0) {
        setVerify(0);
      }
      if (res.count >= 1) {
        if (res.results[0].is_exclusive_shopper) {
          setVerify(2);
        } else {
          setVerify(1);
          setUserId(res.results[0].id);
          setFirstName(res.results[0].firstName || res.results[0].first_name);
          setLastName(res.results[0].lastName || res.results[0].last_name);
          setEmail(res.results[0].email);
          setDateTime(dayjs().format('YYYY-MM-DD HH:mm:ss'));
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const [error, setError] = useState({
    user: [],
    active: [],
    branch_store: [],
    order_type: [],
  });

  const onCreate = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    try {
      const theShopper = await createShoppers({
        id: storeId,
        token: getToken(),
        params: {
          user: userId,
          active: 'True',
          branch_store: storeId.toString(),
          order_type: jobAssignment,
        },
      });
      await dispatch(createShopper(theShopper));
      await onClose(event, setSearchValue(''), setVerify(''));
    } catch (error) {
      if (error.response && error.response.status === 401) {
        deleteToken();
        return;
      }
      if (error.response && error.response.data && error.response.data.user) {
        setErrorMessage(error.response.data.user);
      } else {
        setErrorMessage('An error occurred while saving.');
      }
      console.error(error);
    }
  };

  const handleExclusiveShapyerProcessRegistration = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    const res = await createShopperRequest({
      id: storeId,
      token: getToken(),
      params: {
        email: searchValue,
        firstName: firstName,
        lastName: lastName,
        orderType: jobAssignment || 'delivery',
      },
    }).catch(error => {
       if (error.response && error.response.data && error.response.data.email) {
          setErrorMessage(error.response.data.email);
       } else {
          setErrorMessage('An error occurred while registering.');
       }
       return null;
    });

    try {
      if (res) {
        setShapyerRequestEmailSent(1);
        // Instantly add the new shopper to the table via Redux
        dispatch(createShopper(res));
        // Reset and close
        onClose();
        setSearchValue('');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const onClose = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    setUserId('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setDateTime('');
    setVerify('');
    setErrorMessage('');
    props.handleClose(event);
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={props.open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}>
      <Fade in={props.open}>
        <ModalWrapper>
          <div className={classes.iconBox}>
            <IconButton aria-label="close" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
          <Title id="transition-modal-title" className={classes.modalTitle}>
            {t('Shoppers.shoppersModal.addShapyer')}
          </Title>

          <Form onSubmit={onCreate}>
            <div>
              <label className={classes.emailLabel}>
                {t('Shoppers.shoppersModal.email')}
              </label>
              <OutlinedInput
                margin="dense"
                className={classes.outlinedInput}
                type="email"
                onChange={(e) => setSearchValue(e.target.value)}
                required
              />
              <Button
                className={classes.searchbutton}
                onClick={handleSearchExclusiveShopper}>
                {t('Shoppers.shoppersModal.searchButton')}
              </Button>
            </div>
            {verify === 0 ? (
              <>
                <Divider className={classes.modalDivider} />
                <p className={classes.usernotfound}>
                  {t('Shoppers.shoppersModal.userNotFound')} Please register them:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginTop: '15px', marginBottom: '15px', width: '100%' }}>
                  <OutlinedInput
                    margin="dense"
                    className={classes.outlinedInput}
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    style={{ width: '90%', maxWidth: '350px' }}
                  />
                  <OutlinedInput
                    margin="dense"
                    className={classes.outlinedInput}
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    style={{ width: '90%', maxWidth: '350px' }}
                  />
                </div>
                <div className={classes.jobAssignmentBox} style={{ margin: '15px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <p className={classes.jobAssignmentTitle} style={{ textAlign: 'center' }}>
                    {t('Shoppers.shoppersModal.jobAssignment')}
                  </p>
                  <FormControl component="fieldset" style={{ display: 'flex', alignItems: 'center' }}>
                    <RadioGroup
                      aria-label="radiogroupjobAssignment"
                      name="radiogroupjobAssignment"
                      value={jobAssignment}
                      onChange={(e) => setJobAssignment(e.target.value)}
                    >
                      <FormControlLabel
                        value="delivery"
                        control={
                          <Radio
                            color="primary"
                            icon={<span className={classes.icon} />}
                            checkedIcon={
                              <span className={clsx(classes.icon, classes.checkedIcon)} />
                            }
                          />
                        }
                        label={
                          <span className={classes.radioLabel}>
                            {t('Shoppers.shoppersModal.delivery')}
                          </span>
                        }
                      />
                      <FormControlLabel
                        value="pickup"
                        control={
                          <Radio
                            color="primary"
                            icon={<span className={classes.icon} />}
                            checkedIcon={
                              <span className={clsx(classes.icon, classes.checkedIcon)} />
                            }
                          />
                        }
                        label={
                          <span className={classes.radioLabel}>
                            {t('Shoppers.shoppersModal.pickup')}
                          </span>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </div>
                <div className={classes.exclusiveShapyerRegisterButtons}>
                  <SmallCancelButton
                    className={classes.buttonCancel}
                    onClick={onClose}
                    style={{ width: 'auto', padding: '5px 15px', minWidth: '120px' }}>
                    {t('Shoppers.shoppersModal.noButton') || 'Cancel'}
                  </SmallCancelButton>
                  <SmallButton
                    className={classes.buttonAdd}
                    onClick={handleExclusiveShapyerProcessRegistration}
                    disabled={!firstName || !lastName}
                    style={{ width: 'auto', padding: '5px 15px', minWidth: '140px' }}
                  >
                    Register & Add
                  </SmallButton>
                </div>
                {errorMessage && (
                  <p style={{ color: 'red', textAlign: 'center', marginTop: '10px', fontWeight: 'bold' }}>
                    {errorMessage}
                  </p>
                )}
                {shapyerRequestEmailSent === 1 && (
                  <p className={classes.emailSent}>
                    User registered successfully!
                  </p>
                )}
              </>
            ) : verify === 2 ? (
              <>
                <Divider className={classes.modalDivider} />
                <p style={{ color: 'red', textAlign: 'center', marginTop: '20px', fontWeight: 'bold' }}>
                  This user already belongs to another store as an exclusive shapyer.
                </p>
              </>
            ) : verify === 1 ? (
              <>
                <Divider className={classes.modalDivider} />
                <div className={classes.shapyerdatamaingrid}>
                  <div className={classes.shapyerdatabox}>
                    <p className={classes.shapyerdatatitle}>
                      {t('Shoppers.shoppersModal.shapyerData')}
                    </p>
                    <p className={classes.shapyerName}>
                      {firstName} {lastName}
                    </p>
                    <p className={classes.shapyerEmail}>{email}</p>
                    <p className={classes.shapyerSignInDate}>
                      {t('Shoppers.shoppersModal.signInDate')} {dateTime}
                    </p>
                  </div>
                  <div className={classes.jobAssignmentBox}>
                    <p className={classes.jobAssignmentTitle}>
                      {t('Shoppers.shoppersModal.jobAssignment')}
                    </p>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="radiogroupjobAssignment"
                        name="radiogroupjobAssignment"
                        value={jobAssignment}
                        onChange={(e) => setJobAssignment(e.target.value)}
                      >
                        <FormControlLabel
                          value="delivery"
                          control={
                            <Radio
                              color="primary"
                              icon={<span className={classes.icon} />}
                              checkedIcon={
                                <span className={clsx(classes.icon, classes.checkedIcon)} />
                              }
                            />
                          }
                          label={
                            <span className={classes.radioLabel}>
                              {t('Shoppers.shoppersModal.delivery')}
                            </span>
                          }
                        />
                        <FormControlLabel
                          value="pickup"
                          control={
                            <Radio
                              color="primary"
                              icon={<span className={classes.icon} />}
                              checkedIcon={
                                <span className={clsx(classes.icon, classes.checkedIcon)} />
                              }
                            />
                          }
                          label={
                            <span className={classes.radioLabel}>
                              {t('Shoppers.shoppersModal.pickup')}
                            </span>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </div>
                </div>
                <div className={classes.exclusiveShapyerRegisterButtons}>
                  <SmallCancelButton
                    className={classes.buttonCancel}
                    onClick={onClose}>
                    {t('Shoppers.shoppersModal.noButton')}
                  </SmallCancelButton>
                  <SmallButton
                    className={classes.buttonAdd}
                    onClick={onCreate}>
                    {t('Shoppers.shoppersModal.yesButton')}
                  </SmallButton>
                </div>
                {errorMessage && (
                  <p style={{ color: 'red', textAlign: 'center', marginTop: '10px', fontWeight: 'bold' }}>
                    {errorMessage}
                  </p>
                )}
              </>
            ) : (
              <div>
                <br />
                <br />
              </div>
            )}
          </Form>
        </ModalWrapper>
      </Fade>
    </Modal>
  );
};

ShoppersModal.propTypes = {
  handleClose: PropTypes.func,
  open: PropTypes.bool,
};
